import React, { useEffect, useState } from "react";
import {
  TradeFlowTrackerContext,
  useTradeFlowTracker,
} from "../../../context/TradeFlowTrackerContext";
import { DealsGridContext, CompleteJson } from "../../../types/DealTracker";
import {
  CheckIcon,
  ChevronRightIcon,
  CrossIcon,
  SpinnerIcon,
} from "../../Icons";
import Drawer from "../../Shared/Drawer";
import {
  TradeFlowDataContext,
  useTradeFlowData,
} from "../../../context/TradeFlowDataContext ";
import { useTradeFlowUpdater } from "../../../hooks/useTradeFlowUpdater";

import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Tabs from "../../Shared/Tabs";
import { useTradeFlow } from "../../../hooks/useTradeFlow";

type Props = {
  rowData: CompleteJson;
  tableContext: DealsGridContext;
  children: React.ReactNode;
};

export default function TradeFlowTracker({
  rowData,
  tableContext,
  children,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [_, setSearchParams] = useSearchParams();

  const isViewer = !["owner", "editor"].includes(tableContext.permission);

  useEffect(() => {
    if (!isOpen) {
      setSearchParams({});
    }
  }, [isOpen]);

  const openTradeFlowTracker = () => {
    const { flow, company_name, row_id } = rowData;

    if (!flow.column_value) {
      toast.warning("Please select a flow");

      return;
    }

    setSearchParams({
      table_id: String(tableContext.table_id),
      company_name: company_name.column_value || "",
      row_id: row_id || "",
    });

    setIsOpen(true);
  };

  return (
    <TradeFlowTrackerContext.Provider
      value={{
        isOpen,
        toggle: () => setIsOpen((p) => !p),
        activeTab,
        setActiveTab,
        rowData,
        tableContext,
        isViewer,
        openTradeFlowTracker,
      }}
    >
      {children}
    </TradeFlowTrackerContext.Provider>
  );
}

TradeFlowTracker.Trigger = function Trigger({ value }: { value: string }) {
  const { openTradeFlowTracker, isOpen } = useTradeFlowTracker();

  return (
    <div className="flex items-center gap-2">
      <button
        className="p-1 hover:scale-95 hover:text-custom-blue-medium hover:bg-gray-300 rounded-lg"
        onClick={openTradeFlowTracker}
        title="Open the flow tracker"
      >
        <ChevronRightIcon
          className={`size-4 ${isOpen ? "rotate-90" : ""}`}
          strokeWidth={2.5}
        />
      </button>
      <span>{value}</span>
    </div>
  );
};

TradeFlowTracker.Drawer = function DrawerWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const { isOpen, toggle } = useTradeFlowTracker();

  return (
    <Drawer
      isOpen={isOpen}
      onClose={toggle}
      position="bottom"
      size="80%"
      className="max-w-[1200px] left-1/2 -translate-x-1/2"
    >
      <div className="h-full p-6 pb-[140px] space-y-10">{children}</div>
    </Drawer>
  );
};

TradeFlowTracker.Title = function () {
  const { rowData } = useTradeFlowTracker();

  return (
    <h3 className="text-sm text-gray-500 flex items-center justify-center gap-1">
      <span>Trade Flow</span>
      <span className="text-gray-400">›</span>
      <span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="size-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z"
          />
        </svg>
      </span>
      <span className="font-medium">{rowData.company_name.column_value}</span>
    </h3>
  );
};

TradeFlowTracker.Header = function Header({
  children,
}: {
  children: React.ReactNode;
}) {
  return <div className="flex items-center justify-between">{children}</div>;
};

TradeFlowTracker.Status = function () {
  const { isFetching } = useTradeFlowData();

  return (
    <div className="w-[63px] flex items-center gap-2 text-sm">
      {isFetching ? (
        <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-blue-600" />
      ) : (
        <>
          <CheckIcon className="size-3 text-green-600" />
          <span className="text-gray-600">Saved</span>
        </>
      )}
    </div>
  );
};

TradeFlowTracker.CloseBtn = function () {
  const { toggle } = useTradeFlowTracker();

  return (
    <button onClick={toggle} className="hover:scale-95 hover:text-red-400">
      <CrossIcon className="size-6" />
    </button>
  );
};

TradeFlowTracker.TabsHeader = function TabsHeader() {
  const { activeTab, setActiveTab, isViewer } = useTradeFlowTracker();

  const tabs = [
    { label: "Summary" },
    {
      label: "Edit Flow",
      disabled: isViewer,
      disabledReason: "You don’t have permission to edit",
    },
    { label: "Contacts" },
    { label: "Tasks" },
    { label: "Document Vault" },
    { label: "AI Automate" },
  ];

  const onChnage = (index: number) => {
    setActiveTab(index);
  };

  return (
    <Tabs.List
      tabs={tabs}
      activeIndex={activeTab}
      onChange={onChnage}
      className="max-w-[700px] mx-auto"
    />
  );
};

TradeFlowTracker.Tabs = function ({ children }: { children: React.ReactNode }) {
  const { activeTab } = useTradeFlowTracker();

  return <Tabs activeIndex={activeTab}>{children}</Tabs>;
};

TradeFlowTracker.Tab = function ({ children }: { children: React.ReactNode }) {
  return <Tabs.Tab>{children}</Tabs.Tab>;
};

TradeFlowTracker.Data = function TradeFlowData({
  children,
}: {
  children: React.ReactNode;
}) {
  const [searchParams] = useSearchParams();
  const { tableContext, setActiveTab } = useTradeFlowTracker();

  const rowId = searchParams.get("row_id");
  const { permission, uid, table_id } = tableContext;

  const { data, isLoading, isError, isFetching, refetch } = useTradeFlow({
    rowId,
    uid,
    permission,
  });

  const updateTradeFlow = useTradeFlowUpdater({
    permission,
    tableId: table_id,
    parentId: uid, // only for shared tables
  });

  if (isLoading) return <TradeFlowTrackerSkeleton />;

  if (isError)
    return (
      <div className="text-center">
        <p className="text-gray-600">Something went wrong!</p>
        <button onClick={refetch} className="underline">
          relaod
        </button>
      </div>
    );

  if (!data) return <p>No data available</p>;

  const openEditTab = () => {
    setActiveTab(1);
  };

  return (
    <TradeFlowDataContext.Provider
      value={{
        tradeFlowData: data.data,
        updateTradeFlow,
        isFetching,
        openEditTab,
        refetch,
      }}
    >
      {children}
    </TradeFlowDataContext.Provider>
  );
};

function TradeFlowTrackerSkeleton() {
  return <div className="animate-pulse h-full bg-slate-300 rounded-xl mb-8" />;
}
